
// Import scheduler patch FIRST before any React code
import './utils/schedulerPatch';

import React from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { patchReactScheduler, setupGlobalErrorHandling } from './utils/errorHandling'

// Apply React scheduler patch before anything else
console.log('Initializing React scheduler patch...');
patchReactScheduler();

// Initialize global error handling
setupGlobalErrorHandling();

console.log('Application starting...');
console.log('Environment:', import.meta.env.MODE);
console.log('Base URL:', import.meta.env.BASE_URL);
console.log('Current Location:', window.location.href);

// Function to handle critical errors during initialization
const reportCriticalError = (error: Error | unknown) => {
  console.error('CRITICAL ERROR:', error);
  
  // Add to global error collection
  if (typeof window !== 'undefined' && window.captureError) {
    window.captureError(error);
  }
  
  // Show the loading error div
  const errorDiv = document.getElementById('loading-error');
  if (errorDiv) {
    errorDiv.style.display = 'block';
    
    // Add technical details
    try {
      const detailsContainer = document.createElement('details');
      detailsContainer.style.marginTop = '20px';
      detailsContainer.style.textAlign = 'left';
      
      const summary = document.createElement('summary');
      summary.textContent = 'Technical Details';
      summary.style.cursor = 'pointer';
      summary.style.color = '#E53E77';
      
      const errorDetails = document.createElement('pre');
      errorDetails.style.backgroundColor = '#f5f5f5';
      errorDetails.style.padding = '10px';
      errorDetails.style.borderRadius = '4px';
      errorDetails.style.overflow = 'auto';
      errorDetails.style.fontSize = '12px';
      errorDetails.textContent = error instanceof Error 
        ? `${error.name}: ${error.message}\n${error.stack || ''}` 
        : String(error);
        
      detailsContainer.appendChild(summary);
      detailsContainer.appendChild(errorDetails);
      errorDiv.appendChild(detailsContainer);
    } catch (e) {
      console.error('Failed to display error details:', e);
    }
  } else {
    // Fallback if the error div doesn't exist
    const newErrorDiv = document.createElement('div');
    newErrorDiv.style.cssText = 'padding: 20px; max-width: 600px; margin: 100px auto; text-align: center; font-family: system-ui, -apple-system, sans-serif;';
    newErrorDiv.innerHTML = `
      <h1 style="color: #E53E77;">Unable to load Pink Panther AI</h1>
      <p>There was a problem loading the application: ${error instanceof Error ? error.message : String(error)}</p>
      <p>Please try refreshing the page or check your internet connection.</p>
      <button onclick="window.location.reload()" style="padding: 8px 16px; background: #E53E77; color: white; border: none; border-radius: 4px; cursor: pointer; margin-top: 20px;">
        Refresh Page
      </button>
      <pre style="text-align: left; margin-top: 20px; background: #f5f5f5; padding: 10px; border-radius: 4px; overflow: auto; font-size: 12px;">
        ${error instanceof Error ? error.stack || error.message : String(error)}
      </pre>
    `;
    document.body.appendChild(newErrorDiv);
  }
};

// Try to load the scheduler directly if bundled separately
try {
  if (typeof window !== 'undefined') {
    const scheduler = (window as any).scheduler;
    if (scheduler) {
      console.log('Direct scheduler detected, this is good');
    } else {
      // Try to load the scheduler from a global namespace
      console.log('No direct scheduler reference found, looking for alternatives');
      
      // Add detailed logging to diagnose scheduler issues
      console.log('Available global objects:', Object.keys(window).filter(key => 
        typeof (window as any)[key] === 'object' && 
        (window as any)[key] !== null
      ).join(', '));
      
      // Check for Ho function specifically
      if (typeof (window as any).Ho === 'function') {
        console.log('Ho function is globally available');
      } else {
        console.log('Ho function is not globally available, using polyfill');
      }
    }
  }
} catch (err) {
  console.warn('Scheduler detection failed:', err);
}

// Use React 18's createRoot API with extensive error handling
const rootElement = document.getElementById('root');
console.log('Root element found:', !!rootElement);

// Add specific error handling for common loading issues
if (!rootElement) {
  reportCriticalError(new Error("Could not find root element with id 'root'"));
} else {
  try {
    console.log('Attempting to import App component...');
    
    // Import the error handling utilities
    import('./utils/diagnostics').then(() => {
      console.log('Diagnostics utilities loaded');
    }).catch(e => console.error('Failed to load diagnostics:', e));
    
    // Dynamically import App for code splitting
    import('./App').then(({ default: App }) => {
      try {
        // Create and render root with improved error boundaries
        console.log('Creating React root...');
        const root = createRoot(rootElement);
        
        console.log('Rendering application...');
        root.render(
          <React.StrictMode>
            <React.Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse flex flex-col items-center">
                  <div className="w-12 h-12 bg-owens-pink/50 rounded-full mb-4"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded"></div>
                </div>
              </div>
            }>
              <App />
            </React.Suspense>
          </React.StrictMode>
        );
        
        // Log successful initialization
        console.log('Application initialization complete');
      } catch (renderError) {
        reportCriticalError(renderError);
      }
    }).catch(importError => {
      reportCriticalError(importError);
    });
  } catch (error) {
    reportCriticalError(error);
  }
}

// Add diagnostics
window.addEventListener('DOMContentLoaded', () => {
  console.log('DOM content loaded');
});

window.addEventListener('load', () => {
  console.log('Window load complete');
  
  // Run network test after page load
  import('./utils/diagnostics').then(({ runNetworkTest, checkResourceLoading }) => {
    runNetworkTest();
    setTimeout(checkResourceLoading, 2000); // Check resources after 2 seconds
  }).catch(e => console.error('Failed to run post-load diagnostics:', e));
});

// Report any runtime errors
window.addEventListener('error', (event) => {
  console.error('Runtime error:', event.error);
  if (window.captureError) {
    window.captureError(event.error || event);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  if (window.captureError) {
    window.captureError(event.reason);
  }
});
